import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

const REMINDER_INTERVAL_MS = 30 * 60 * 1000;
const REMINDER_CHECK_INTERVAL_MS = 60 * 1000;
const REMINDER_TYPES = [
  { key: 'word', title: 'Word of the Day', message: "You haven't visited today's Word of the Day yet.", path: '/language-hub/daily-challenge' },
  { key: 'idioms', title: 'Idioms', message: "You haven't explored Idioms today.", path: '/language-hub/idioms' }
] as const;

type Reminder = typeof REMINDER_TYPES[number];

const getLocalDateKey = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const LanguageHubVisitReminders: React.FC<{ userId?: string }> = ({ userId }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [reminderQueue, setReminderQueue] = useState<Reminder[]>([]);

  const markPageVisited = useCallback((dateKey: string) => {
    if (!userId) return;
    const visitedType = REMINDER_TYPES.find((reminder) => location.pathname.startsWith(reminder.path));
    if (visitedType) {
      localStorage.setItem(`languageHubReminder:${userId}:${dateKey}:${visitedType.key}:visited`, 'true');
      setReminderQueue((queue) => queue.filter((reminder) => reminder.key !== visitedType.key));
    }
  }, [location.pathname, userId]);

  useEffect(() => {
    if (!userId) return;
    markPageVisited(getLocalDateKey(new Date()));
  }, [location.pathname, markPageVisited, userId]);

  useEffect(() => {
    if (!userId) return;

    const checkReminders = () => {
      if (document.visibilityState !== 'visible') return;
      const dateKey = getLocalDateKey(new Date());
      markPageVisited(dateKey);
      if (localStorage.getItem('notificationsEnabled') === 'false') return;

      const now = Date.now();
      REMINDER_TYPES.forEach((reminder) => {
        const prefix = `languageHubReminder:${userId}:${dateKey}:${reminder.key}`;
        if (localStorage.getItem(`${prefix}:visited`) === 'true') return;

        const startedAtKey = `${prefix}:startedAt`;
        const lastReminderKey = `${prefix}:lastReminderAt`;
        const startedAt = Number(localStorage.getItem(startedAtKey)) || now;
        if (!localStorage.getItem(startedAtKey)) {
          localStorage.setItem(startedAtKey, String(now));
          return;
        }
        const previousReminder = Number(localStorage.getItem(lastReminderKey)) || startedAt;
        if (now - previousReminder < REMINDER_INTERVAL_MS) return;

        localStorage.setItem(lastReminderKey, String(now));
        if ('Notification' in window && Notification.permission === 'granted') {
          try {
            const notification = new Notification(reminder.title, { body: reminder.message });
            notification.onclick = () => {
              window.focus();
              navigate(reminder.path);
              notification.close();
            };
          } catch (error) {
            console.error('Unable to show a browser reminder notification:', error);
            setReminderQueue((queue) => [...queue, reminder]);
          }
        } else {
          setReminderQueue((queue) => [...queue, reminder]);
        }
      });
    };

    checkReminders();
    const interval = window.setInterval(checkReminders, REMINDER_CHECK_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [markPageVisited, navigate, userId]);

  const currentReminder = reminderQueue[0];
  const dismissReminder = () => setReminderQueue((queue) => queue.slice(1));

  return (
    <Snackbar
      open={Boolean(currentReminder)}
      autoHideDuration={12000}
      onClose={dismissReminder}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ zIndex: (theme) => theme.zIndex.snackbar + 1 }}
    >
      {currentReminder ? (
        <Alert
          severity="info"
          variant="filled"
          onClose={dismissReminder}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                navigate(currentReminder.path);
                dismissReminder();
              }}
            >
              Open
            </Button>
          }
        >
          {currentReminder.message}
        </Alert>
      ) : undefined}
    </Snackbar>
  );
};

export default LanguageHubVisitReminders;
